import { buttonStyle, ICON_SLOT } from 'components/buttons/buttonStyle';
import ListCreateButton from 'components/buttons/ListCreateButton';
import EmployeeName from 'components/employee/EmployeeName';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import { Add, Delete as DeleteIcon, Download, Edit as EditIcon, MoreVert, Sync } from 'components/icons';
import EntityLinkList from 'components/misc/EntityLinkList';
import { FormattedDate } from 'components/misc/FormattedDate';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { endOfDay, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
    useDeleteMatterInsiderMutation,
    useGetMatterInsiderListExportLanguagesQuery,
    useGetMatterInsiderListQuery,
    useLazyGetMatterInsiderListExportQuery,
    useRefreshMatterInsiderMutation,
} from 'store/api/matter-insider-list';
import { MatterInsiderRow } from 'types/matter-insider-list';
import { formatFullAddress } from 'utils/addressFormatter';
import { successAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { InsiderDialog, useAddableKinds } from './InsiderDialog';
import { exportInsiderListXlsx } from './insiderListExcel';
import { EMPTY, formatLocalMinute, text } from './insiderListFormat';

const UPDATE = ['UPDATE.MATTER.INSIDER_LIST'];
const DELETE = ['DELETE.MATTER.INSIDER_LIST'];
const PRINT = ['PRINT.MATTER.INSIDER_LIST'];

/** An access time on one line: the format is fixed, and the column's `minPx` is sized to fit it. */
function LocalMinuteCell({ instant }: { instant: string }) {
    return <span className="whitespace-nowrap">{formatLocalMinute(instant)}</span>;
}

/**
 * The name in the app's name order, linked to the employee's page on an employee's row, with a chip under
 * it saying where a hand-added row came from. A deleted employee's row carries no name, so no link.
 */
function NameCell({ insider }: { insider: MatterInsiderRow }) {
    const firstName = insider.firstName ?? undefined;
    const lastName = insider.lastName ?? undefined;
    const name = [firstName, lastName].filter(Boolean).join(' ');
    return (
        <div className="flex flex-col items-start gap-1">
            {insider.employeeId && name ? (
                <EntityLinkList
                    domain="employee"
                    newTab
                    items={[{ id: insider.employeeId, name, firstName, lastName }]}
                />
            ) : (
                <span>{name ? <EmployeeName entity={insider} /> : EMPTY}</span>
            )}
            {insider.isManuallyAdded &&
                (insider.employeeId ? (
                    <Badge className="bg-grey-200 text-grey-600">{t('matters.insider-list.added-manually')}</Badge>
                ) : (
                    <Badge className="bg-info-lighter text-info-darker">{t('matters.insider-list.non-employee')}</Badge>
                ))}
        </div>
    );
}

/**
 * The Excel export's button, downloading the list shown, dated or not, in a language the export has labels in. With
 * one such language the button downloads it; with two or more it opens a menu of them, each named by its LANGUAGE
 * classifier (the code until the classifiers load); with none there is no button.
 */
function ExportMenu({ matterId, asOf, asOfDate }: { matterId: string; asOf?: string; asOfDate: string | null }) {
    const { data: languages = [] } = useGetMatterInsiderListExportLanguagesQuery(matterId);
    const { classifiers } = useClassifiers({ codes: ['LANGUAGE'] });
    const [fetchExport, { isFetching }] = useLazyGetMatterInsiderListExportQuery();

    async function download(langCode: string): Promise<void> {
        try {
            const data = await fetchExport({ matterId, langCode, asOf }).unwrap();
            await exportInsiderListXlsx(data, asOfDate ?? undefined);
        } catch {
            // The API layer has already shown why.
        }
    }

    if (languages.length === 0) {
        return null;
    }
    const label = (
        <>
            <span className={ICON_SLOT.small}>
                <Download />
            </span>
            {t('matters.insider-list.export-excel')}
        </>
    );
    if (languages.length === 1) {
        return (
            <Button
                {...buttonStyle({ variant: 'outlined', size: 'small' })}
                disabled={isFetching}
                onClick={() => void download(languages[0])}
            >
                {label}
            </Button>
        );
    }
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={<Button {...buttonStyle({ variant: 'outlined', size: 'small' })} disabled={isFetching} />}
            >
                {label}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-auto">
                {languages.map((langCode) => (
                    <DropdownMenuItem key={langCode} onClick={() => void download(langCode)}>
                        {getClassifierText(classifiers['LANGUAGE'], langCode)}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * The case page's insider list: everyone who has had access to the case, and when. The case team's rows
 * are written by the server as the team changes; the rest are added here by hand, and only those can be
 * removed. Which rows those are is read off each fetched row, never kept locally: the server turns a
 * hand-added row into a case-team one when the person gains a case role.
 *
 * A day picked shows the list as it stood at the end of that day in the viewer's time zone, read-only, with a
 * button back to the current list in the Add button's place.
 *
 * Mount it only where its tab is visible: with the MATTER_INSIDER_LIST flag off, every endpoint it and
 * its dialog call answers 403.
 */
export function InsiderListView({ matterId }: { matterId: string }) {
    const canUpdate = useHasPermissions(UPDATE);
    const canDelete = useHasPermissions(DELETE);
    const canPrint = useHasPermissions(PRINT);
    const { control, setValue } = useForm<{ asOfDate: string | null }>({ defaultValues: { asOfDate: null } });
    // `yyyy-MM-dd` once a whole date is in; an entry still being typed reads as '' and keeps the current list.
    const asOfDate = useWatch({ control, name: 'asOfDate' }) || null;
    const asOf = asOfDate ? endOfDay(parseISO(asOfDate)).toISOString() : undefined;
    const isDated = asOf !== undefined;
    const kinds = useAddableKinds();
    const formatName = useEmployeeNameFormatter();
    const { classifiers } = useClassifiers({ codes: ['COUNTRY'] });
    // Refetched on every visit: editing the case team rewrites the list server-side, and nothing on
    // the case form invalidates it.
    const { data: rows = [], isLoading } = useGetMatterInsiderListQuery(
        { matterId, asOf },
        { refetchOnMountOrArgChange: true }
    );
    const [editing, setEditing] = useState<MatterInsiderRow | 'new' | null>(null);
    const [removing, setRemoving] = useState<MatterInsiderRow | null>(null);
    const [refreshInsider] = useRefreshMatterInsiderMutation();

    const listedEmployeeIds = useMemo(
        () => new Set(rows.flatMap((row) => (row.employeeId ? [row.employeeId] : []))),
        [rows]
    );

    // No column has an accessor, so none is sortable: the server's order (access obtained, oldest
    // first) is the only one.
    const columns = useMemo((): DataTableColumnDef<MatterInsiderRow>[] => {
        const dataColumns: DataTableColumnDef<MatterInsiderRow>[] = [
            {
                id: 'name',
                header: t('matters.insider-list.name'),
                // Wide enough for the widest chip: the cell clips what passes its edge, and this table
                // offers no resize to go narrower.
                meta: { sticky: true, minPx: 200 },
                cell: ({ row }) => <NameCell insider={row.original} />,
            },
            {
                id: 'workPhone',
                header: t('matters.insider-list.work-phone'),
                meta: { minPx: 170 },
                cell: ({ row }) => text(row.original.workPhone),
            },
            {
                id: 'company',
                header: t('matters.insider-list.company'),
                meta: { widthWeight: 1.5, minPx: 200 },
                cell: ({ row }) => text(row.original.companyName),
            },
            {
                id: 'functionAndReason',
                header: t('matters.insider-list.function-and-reason'),
                meta: { widthWeight: 2, minPx: 240 },
                cell: ({ row }) => text(row.original.functionAndReason),
            },
            {
                id: 'accessObtained',
                header: t('matters.insider-list.access-obtained'),
                meta: { minPx: 210 },
                cell: ({ row }) => <LocalMinuteCell instant={row.original.accessObtained} />,
            },
            {
                id: 'accessCeased',
                header: t('matters.insider-list.access-ceased'),
                meta: { minPx: 210 },
                // A green badge rather than a blank, so current access never reads as missing data.
                cell: ({ row }) =>
                    row.original.accessCeased ? (
                        <LocalMinuteCell instant={row.original.accessCeased} />
                    ) : (
                        <Badge className="bg-success-light text-fg-success">{t('matters.insider-list.current')}</Badge>
                    ),
            },
            {
                id: 'dateOfBirth',
                header: t('matters.insider-list.date-of-birth'),
                meta: { minPx: 120 },
                cell: ({ row }) => <FormattedDate value={row.original.dateOfBirth} fallback={EMPTY} />,
            },
            {
                id: 'personalId',
                header: t('matters.insider-list.national-id'),
                meta: { minPx: 150 },
                cell: ({ row }) => text(row.original.personalId),
            },
            {
                id: 'mobilePhone',
                header: t('matters.insider-list.personal-phone'),
                meta: { minPx: 170 },
                cell: ({ row }) => text(row.original.mobilePhone),
            },
            {
                id: 'homeAddress',
                header: t('matters.insider-list.home-address'),
                meta: { widthWeight: 2, minPx: 240 },
                cell: ({ row }) =>
                    row.original.homeAddress ? formatFullAddress(row.original.homeAddress, classifiers) : EMPTY,
            },
        ];
        if (isDated || (!canUpdate && !canDelete)) {
            return dataColumns;
        }
        return [
            ...dataColumns,
            {
                id: 'actions',
                header: '',
                meta: { fixedPx: 64, textAlign: 'right' },
                cell: ({ row }) => {
                    const insider = row.original;
                    // The case team's rows are the record of its access: only a hand-added row can go, and
                    // not while its employee holds a case role (the server answers 400 then).
                    const canRemove = canDelete && insider.isManuallyAdded && insider.currentCaseRoles.length === 0;
                    // A deleted employee's row carries no name, and has no profile left to update from.
                    const canRefresh = canUpdate && !!insider.employeeId && !!(insider.firstName || insider.lastName);
                    if (!canUpdate && !canRemove) {
                        return null;
                    }
                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                render={<Button variant="ghost" size="icon-sm" aria-label={t('actions')} />}
                            >
                                <MoreVert />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-auto">
                                {canUpdate && (
                                    <DropdownMenuItem onClick={() => setEditing(insider)}>
                                        <EditIcon />
                                        {t('edit')}
                                    </DropdownMenuItem>
                                )}
                                {canRefresh && (
                                    <DropdownMenuItem onClick={() => void refreshInsider({ matterId, id: insider.id })}>
                                        <Sync />
                                        {t('update')}
                                    </DropdownMenuItem>
                                )}
                                {canRemove && (
                                    <DropdownMenuItem variant="destructive" onClick={() => setRemoving(insider)}>
                                        <DeleteIcon />
                                        {t('remove')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                },
            },
        ];
    }, [canUpdate, canDelete, classifiers, isDated, matterId, refreshInsider]);

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                    <h5 className="text-h5">{t('matters.insider-list.title')}</h5>
                    <p className="text-fg-secondary text-sm">
                        {t(isDated ? 'matters.insider-list.dated-caption' : 'matters.insider-list.caption')}
                    </p>
                </div>
                {/* One row of 40px controls, the picker's label beside it as on the app's other toolbar filters. */}
                <div className="flex items-center gap-2">
                    <label htmlFor="asOfDate" className="text-fg-secondary whitespace-nowrap">
                        {t('matters.insider-list.as-of')}
                    </label>
                    <DateInput
                        control={control}
                        name="asOfDate"
                        placeholder="matters.insider-list.as-of-placeholder"
                        maxDate={new Date()}
                        fullWidth
                        wrapperClassName="w-44 [&_[data-slot=input-group]]:h-10"
                    />
                    {canPrint && <ExportMenu matterId={matterId} asOf={asOf} asOfDate={asOfDate} />}
                    {isDated ? (
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary', size: 'small' })}
                            onClick={() => setValue('asOfDate', null)}
                        >
                            {t('matters.insider-list.current-version')}
                        </Button>
                    ) : (
                        (kinds.employee || kinds.nonEmployee) && (
                            <ListCreateButton
                                permissions={UPDATE}
                                variant="contained"
                                color="secondary"
                                size="small"
                                startIcon={<Add />}
                                onClick={() => setEditing('new')}
                                labelId="matters.insider-list.add"
                                newLabelId="new-entity.insider"
                            />
                        )
                    )}
                </div>
            </div>
            <DataTable<MatterInsiderRow>
                columns={columns}
                data={rows}
                getRowId={(row) => String(row.id)}
                columnConfigId="matter-insider-list"
                // A chip under the name and wrapped free text make rows of different heights; the
                // engine centres cell content, which would float single-line cells away from the name.
                className="min-h-0 flex-1 [&_tbody_td]:items-start!"
                isLoading={isLoading}
            />
            {editing && (
                <InsiderDialog
                    matterId={matterId}
                    row={editing === 'new' ? undefined : editing}
                    listedEmployeeIds={listedEmployeeIds}
                    onClose={() => setEditing(null)}
                />
            )}
            {removing && (
                <RemoveInsiderDialog
                    matterId={matterId}
                    insider={removing}
                    name={formatName(removing)}
                    onClose={() => setRemoving(null)}
                />
            )}
        </div>
    );
}

function RemoveInsiderDialog({
    matterId,
    insider,
    name,
    onClose,
}: {
    matterId: string;
    insider: MatterInsiderRow;
    name: string;
    onClose: () => void;
}) {
    const [deleteInsider, { isLoading }] = useDeleteMatterInsiderMutation();

    // A failed removal keeps the dialog open; the API layer has already shown the server's reason.
    async function remove(): Promise<void> {
        try {
            await deleteInsider({ matterId, id: insider.id }).unwrap();
        } catch {
            return;
        }
        successAlert(t('matters.insider-list.removed', { name }));
        onClose();
    }

    return (
        <AlertDialog open onOpenChange={(open) => !open && onClose()}>
            <AlertDialogContent className="data-[size=default]:sm:max-w-[440px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('matters.insider-list.remove-title', { name })}</AlertDialogTitle>
                    <AlertDialogDescription>{t('matters.insider-list.remove-body')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" disabled={isLoading} onClick={() => void remove()}>
                        {t('remove')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
